// PUBLIC contact-onboarding portal (v2 /portal/{token} parity) — a contact opens the
// link a company sent them and completes their own details. No login: the token is the
// authorisation, so this page uses plain fetch (the api client's auth machinery would
// bounce an anonymous visitor to /login).
import { html, signal } from '../shared/t4.js';
import { t } from '../shared/i18n.js';

// [field, English label, placeholder, translation key]. Rows without a key have no matching
// admin Word yet, so they stay in English until one is added.
const FIELDS = [
  ['contact_person', 'Contact person', 'e.g. Jane Doe', 'portal.contact_person'],
  ['email', 'Email', 'e.g. jane@blue.co.za', 'portal.email'],
  ['phone', 'Tel', 'e.g. 011 000 0000', 'portal.tel'],
  ['mobile', 'Cell', 'e.g. 082 000 0000', 'common.cell'],
  ['fax', 'Fax', 'e.g. 011 000 0001', 'portal.fax'],
  ['website_url', 'Website', 'e.g. www.blue.co.za', 'portal.website'],
  ['address_line1', 'Street', 'e.g. 12 Main Rd', 'portal.street'],
  ['address_line2', 'Suburb', 'e.g. Morningside', 'portal.suburb'],
  ['address_town', 'Town', 'e.g. Durban', 'portal.town'],
  ['address_postal', 'Postal code', 'e.g. 4001', 'portal.postal'],
  ['vat_no', 'VAT no', 'e.g. 4990001234', 'portal.vat_no'],
  ['reg_no', 'Company reg no', 'e.g. 2019/123456/07', 'portal.reg_no'],
  ['tax_no', 'Tax no', 'e.g. 9012345678', 'portal.tax_no'],
  ['bank_name', 'Bank', 'e.g. FNB', 'portal.bank'],
  ['bank_account_no', 'Bank account no', 'e.g. 62010011234', 'portal.bank_account_no'],
  ['bank_branch_code', 'Branch code', 'e.g. 250655', 'portal.branch_code'],
];

export function portalScreen(token){
  const data = signal(null), loading = signal(true), err = signal('');
  const saving = signal(false), saved = signal('');
  const f = {};
  FIELDS.forEach(([k]) => { f[k] = signal(''); });

  (async () => {
    try {
      const res = await fetch('/api/portal/' + encodeURIComponent(token));
      const body = await res.json();
      if(!res.ok) throw new Error(body.error || t('portal.invalid_link', 'This link is not valid'));
      data.value = body;
      FIELDS.forEach(([k]) => { f[k].value = (body.fields || {})[k] || ''; });
    } catch(e){ err.value = e.message || t('portal.invalid_link', 'This link is not valid'); }
    loading.value = false;
  })();

  async function save(e){
    e.preventDefault();
    saving.value = true; saved.value = '';
    const body = {};
    FIELDS.forEach(([k]) => { body[k] = f[k].value.trim(); });
    try {
      const res = await fetch('/api/portal/' + encodeURIComponent(token), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body) });
      const out = await res.json();
      if(!res.ok) throw new Error(out.error || t('portal.save_failed', 'Could not save'));
      saved.value = out.message || t('portal.saved', 'Saved — thank you!');
    } catch(e2){ saved.value = '⚠ ' + (e2.message || t('portal.save_failed', 'Could not save')); }
    saving.value = false;
  }

  const body = () => {
    if(loading.value) return html`<div class="muted-block">${t('portal.checking', 'Checking your link…')}</div>`;
    if(err.value) return html`<div class="portal-card">
      <h1 class="page-title">${t('portal.expired_title', 'Link expired')}</h1>
      <p class="page-sub">${err.value}. ${t('portal.expired_body', 'Ask the company that sent it for a fresh link.')}</p>
    </div>`;
    const d = data.value, ok = saved.value;
    return html`<div class="portal-card">
      <h1 class="page-title">${t('portal.greeting', 'Hi')} ${d.contact_name}</h1>
      <p class="page-sub"><b>${d.company_name}</b> ${t('portal.intro', 'uses Sleek for their books and asked you to confirm or complete your details below. Only you can see this page; the link expires')} ${String(d.expires_at || '').slice(0, 10)}.</p>
      <form @submit=${save}>
        <div class="portal-grid">
          ${FIELDS.map(([k, label, ph, tk]) => html`<label class="ct-field"><span class="jrn-lbl">${tk ? t(tk, label) : label}</span>
            <input class="form-control" .value=${f[k]} @input=${e => { f[k].value = e.target.value; }} placeholder=${ph}></label>`)}
        </div>
        <div class="portal-actions">
          ${ok ? html`<span class=${ok.startsWith('⚠') ? 'row-err' : 't-in fw-700'}>${ok}</span>` : ''}
          <span class="jrn-spacer"></span>
          <button class="btn btn-primary" type="submit" ?disabled=${() => saving.value}>${() => saving.value ? t('common.saving', 'Saving…') : t('portal.save_details', 'Save my details')}</button>
        </div>
      </form>
    </div>`;
  };

  return html`<div class="portal-page">${body}</div>`;
}
